import { Link } from 'found';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import BadgeTradeDirection from 'src/components/Badge/BadgeTradeDirection';
import BadgeTradePointType from 'src/components/Badge/BadgeTradePointType';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import { useTradeRuleCounterParty } from 'src/hooks/useTradeRuleCounterParty';
import getUserDisplayName from 'src/util/i18n/decorators/getUserDisplayName';
import buildTimeRangeSummary from 'src/util/i18n/timeRange';
import { getMemberNodes } from 'src/util/mainDataBuilder';
import { hasSharedConditions as hasSharedTimeOfUseConditions } from 'src/util/timeOfUse';

import UUID from 'src/components/UUID';
import AdminMeterTradeRulesTableItemClause from '../AdminMeterTradeRulesTableItemClause';

const AdminMeterTradeRulesItemFragment = graphql`
  fragment AdminMeterTradeRulesItem_rule on TradeRule {
    id
    buyer {
      user { id email givenName familyName }
      community { id title }
      residualId
      tradePoint { id type }
    }
    seller {
      user { id email givenName familyName }
      community { id title }
      residualId
      tradePoint { id type }
    }
    clauses {
      edges {
        node {
          ...AdminMeterTradeRulesTableItemClause_clause
        }
      }
    }
    tradeType
    state
    start
    finish
    proposedAt
    proposedBy { id email givenName familyName }
    acceptedAt
    acceptedBy { id email givenName familyName }
    rejectedAt
    rejectedBy { id email givenName familyName }
    cancelledAt
    cancelledBy { id email givenName familyName }
    closedAt
    closedBy { id email givenName familyName }
  }
`;

/**
 * AdminMeterTradeRulesItem functional component
 * @param {object} props - Component props
 * @returns {React.ReactElement} - AdminMeterTradeRulesItem component
 */
function AdminMeterTradeRulesItem(props) {
  const {
    propertyId, tradePointId,
  } = props;
  let { rule } = props;
  rule = useFragment(AdminMeterTradeRulesItemFragment, rule);
  const intl = useIntl();

  if (!props) {
    return <Loading />;
  }

  const {
    id, tradeType, state, start, finish,
  } = rule;

  const active = { start, finish };
  const { clauses } = rule;
  const clausesSortedByPrice = (!clauses || !clauses.edges)
    ? [] : rule.clauses.edges.map((el) => (el.node)).sort((a, b) => (b.price - a.price));

  const clauseNodes = getMemberNodes(clauses);
  const showConditions = hasSharedTimeOfUseConditions(clauseNodes);

  const { direction, trader, tradePoint } = useTradeRuleCounterParty(rule, tradePointId) || {};

  return (
    <tr key={`trade-rule-${id}`}>
      <th>
        <Link to={`/properties/${propertyId}/trade-rules/${id}`}>
          <UUID uuid={id} />
        </Link>
        <br />
        <BadgeTradeDirection direction={direction} />
        <br />
        <BadgeTradeType type={tradeType} />
        <br />
        <BadgeTradeRuleState state={state} />
      </th>
      <td>
        {trader && (
          <>
            <strong>{getUserDisplayName(intl, trader)}</strong>
            <br />
            <UUID uuid={trader.id} />
          </>
        )}
      </td>
      <td>
        {tradePoint && (
          <>
            <strong>{tradePoint.label}</strong>
            <br />
            <UUID uuid={tradePoint.tradePoint.id} />
            <br />
            <BadgeTradePointType type={tradePoint.tradePoint.type} />
          </>
        )}
      </td>
      <td>
        <ul className="ps-2">
          {clausesSortedByPrice?.map((clause) => (
            <AdminMeterTradeRulesTableItemClause
              clause={clause}
              key={clause.id}
              showConditions={showConditions}
            />
          ))}
        </ul>
      </td>
      <td>
        {buildTimeRangeSummary(intl, active)}
      </td>
    </tr>
  );
}

AdminMeterTradeRulesItem.propTypes = {
  tradePointId: PropTypes.string.isRequired,
  propertyId: PropTypes.string.isRequired,
  rule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default AdminMeterTradeRulesItem;
